import { HanamiElement, createShadow, define, isBrowser } from '../core/base';
import { deepActiveElement, getFocusable, lockScroll, trapFocus, unlockScroll } from '../core/focus';
import { applyPosition, computePosition, hidePopover, showPopover } from '../core/position';
import { t } from '../core/strings';

const css = `:host{box-sizing:border-box}*,*::before,*::after{box-sizing:inherit}.panel{position:relative;display:flex;flex-direction:column;width:100%;height:100%;overflow:hidden;border-inline-end:1px solid var(--hn-color-border);background:var(--hn-sidebar-bg,var(--hn-color-surface))}:host([placement="end"]) .panel{border-inline-end:0;border-inline-start:1px solid var(--hn-color-border)}.header{display:flex;align-items:center;gap:var(--hn-space-2);min-height:4rem;padding:var(--hn-space-3) var(--hn-space-3) var(--hn-space-2);overflow:hidden}.header ::slotted(*){min-width:0}.body{flex:1;min-height:0;overflow-x:hidden;overflow-y:auto;padding:var(--hn-space-2) var(--hn-space-3) var(--hn-space-4);scrollbar-width:thin;scrollbar-color:var(--hn-color-border-strong) transparent;overscroll-behavior:contain}.footer{display:flex;flex-direction:column;gap:var(--hn-space-2);padding:var(--hn-space-3);overflow:hidden;border-top:1px solid var(--hn-color-border)}.footer.empty-slot{padding-top:var(--hn-space-2)}.toggle{display:grid;place-items:center;align-self:flex-end;width:2.25rem;height:2.25rem;padding:0;border:0;border-radius:var(--hn-radius-md);background:transparent;color:var(--hn-color-text-muted);cursor:pointer;transition:background-color var(--hn-duration-fast) var(--hn-ease-standard),color var(--hn-duration-fast) var(--hn-ease-standard)}.toggle:hover{background:var(--hn-color-hover);color:var(--hn-color-text)}.toggle:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}.toggle svg{width:1.25rem;height:1.25rem;transition:transform var(--hn-duration-slow) var(--hn-ease-bloom)}:host([placement="end"]) .toggle svg{transform:scaleX(-1)}:host([collapsed]:not([mobile])) .toggle{align-self:center}:host([collapsed]:not([mobile])) .toggle svg{transform:rotate(180deg)}:host([placement="end"][collapsed]:not([mobile])) .toggle svg{transform:none}:host([no-toggle]) .toggle{display:none}.icon-close{display:none}:host([mobile]) .icon-collapse{display:none}:host([mobile]) .icon-close{display:block}.backdrop{display:none}:host([mobile]) .backdrop{display:block;position:fixed;inset:0;background:var(--hn-color-overlay);opacity:0;transition:opacity var(--hn-duration-slow) var(--hn-ease-standard)}:host([mobile][open]) .backdrop{opacity:1}:host([mobile]) .panel{position:fixed;inset:0 auto 0 0;width:min(var(--hn-sidebar-width,17rem),86vw);box-shadow:var(--hn-shadow-xl);transform:translateX(-100%);visibility:hidden;transition:transform var(--hn-duration-slow) var(--hn-ease-bloom),visibility 0s linear var(--hn-duration-slow)}:host([mobile][placement="end"]) .panel{inset:0 0 0 auto;transform:translateX(100%)}:host([mobile][open]) .panel{transform:none;visibility:visible;transition:transform var(--hn-duration-slow) var(--hn-ease-bloom),visibility 0s}.flyout{position:fixed;inset:auto;margin:0;padding:0.375rem 0.625rem;border:0;border-radius:var(--hn-radius-sm);background:var(--hn-color-text);color:var(--hn-color-bg);box-shadow:var(--hn-shadow-md);font:var(--hn-weight-medium) 0.8125rem/1.3 var(--hn-font-body);white-space:nowrap;pointer-events:none;overflow:visible}.flyout:not([data-open]){display:none}@media (prefers-reduced-motion:reduce){.panel,.backdrop,.toggle svg{transition-duration:1ms !important}}`;

const html = /* html */ `
  <div class="backdrop" part="backdrop"></div>
  <div class="panel" part="panel">
    <div class="header" part="header"><slot name="header"></slot></div>
    <div class="body" part="body"><slot></slot></div>
    <div class="footer" part="footer">
      <slot name="footer"></slot>
      <button class="toggle" part="toggle" type="button">
        <svg class="icon-collapse" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m11 17-5-5 5-5"/><path d="m18 17-5-5 5-5"/></svg>
        <svg class="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
    </div>
  </div>
  <div class="flyout" part="flyout" aria-hidden="true"></div>
`;

export interface SidebarToggleDetail {
  collapsed: boolean;
}

/**
 * Barre latérale repliable et responsive.
 *
 * Attributs : collapsed, open (tiroir mobile), breakpoint (px, défaut 1024),
 * persist="clé" (mémorise l'état replié), placement="start|end", no-toggle, label.
 * Événements : hn-toggle (repli), hn-open / hn-close (tiroir mobile).
 */
export class HnSidebar extends HanamiElement {
  static readonly tagName = 'hn-sidebar';
  static get observedAttributes() {
    return ['collapsed', 'open', 'breakpoint', 'label'];
  }

  private media?: MediaQueryList;
  private releaseTrap?: () => void;
  private returnFocus?: Element | null;
  private flyoutTarget?: Element;
  private toggleButton!: HTMLButtonElement;
  private flyout!: HTMLElement;
  private initialised = false;

  get collapsed(): boolean {
    return this.getBool('collapsed');
  }
  set collapsed(value: boolean) {
    this.setBool('collapsed', value);
  }

  /** Tiroir ouvert (uniquement sous le point de rupture). */
  get open(): boolean {
    return this.getBool('open');
  }
  set open(value: boolean) {
    this.setBool('open', value);
  }

  /** True sous le point de rupture (mode tiroir). Lecture seule. */
  get mobile(): boolean {
    return this.hasAttribute('mobile');
  }

  get breakpoint(): number {
    return this.getNumber('breakpoint', 1024);
  }
  set breakpoint(value: number) {
    this.setAttribute('breakpoint', String(value));
  }

  connectedCallback(): void {
    this.upgradeProperties('collapsed', 'open', 'breakpoint');
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.toggleButton = root.querySelector('.toggle')!;
      this.flyout = root.querySelector('.flyout')!;
      if ('popover' in this.flyout) this.flyout.setAttribute('popover', 'manual');
      this.toggleButton.addEventListener('click', () => this.toggle());
      root.querySelector('.backdrop')!.addEventListener('click', () => this.hide());
      const footerSlot = root.querySelector<HTMLSlotElement>('slot[name="footer"]')!;
      const footer = root.querySelector('.footer')!;
      const syncFooter = () => footer.classList.toggle('empty-slot', footerSlot.assignedNodes().length === 0);
      footerSlot.addEventListener('slotchange', syncFooter);
      syncFooter();
    }
    if (!this.hasAttribute('role')) this.setAttribute('role', 'navigation');
    this.syncLabel();

    const key = this.storageKey;
    if (key && !this.initialised) {
      try {
        const saved = localStorage.getItem(key);
        if (saved !== null) this.collapsed = saved === '1';
      } catch {
        /* stockage indisponible */
      }
    }
    this.initialised = true;

    this.watchBreakpoint();
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onPointerOut);
    this.syncToggleLabel();
  }

  disconnectedCallback(): void {
    this.media?.removeEventListener('change', this.onMediaChange);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onPointerOut);
    this.deactivateDrawer();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.shadowRoot || oldValue === newValue) return;
    switch (name) {
      case 'collapsed': {
        const key = this.storageKey;
        if (key) {
          try {
            localStorage.setItem(key, this.collapsed ? '1' : '0');
          } catch {
            /* stockage indisponible */
          }
        }
        this.hideFlyout();
        this.syncToggleLabel();
        this.emit<SidebarToggleDetail>('hn-toggle', { collapsed: this.collapsed });
        break;
      }
      case 'open':
        if (this.mobile) {
          if (this.open) this.activateDrawer();
          else this.deactivateDrawer(true);
        }
        this.syncToggleLabel();
        break;
      case 'breakpoint':
        this.watchBreakpoint();
        break;
      case 'label':
        this.syncLabel();
        break;
    }
  }

  /** Replie/déplie (bureau) ou ouvre/ferme le tiroir (mobile). */
  toggle(): void {
    if (this.mobile) this.open = !this.open;
    else this.collapsed = !this.collapsed;
  }

  show(): void {
    if (this.mobile) this.open = true;
    else this.collapsed = false;
  }

  hide(): void {
    if (this.mobile) this.open = false;
  }

  private get storageKey(): string | null {
    const key = this.getAttribute('persist');
    return key ? `hn-sidebar:${key}` : null;
  }

  private syncLabel(): void {
    this.setAttribute('aria-label', this.getAttribute('label') || t('sidebar'));
  }

  private syncToggleLabel(): void {
    if (!this.toggleButton) return;
    const label = this.mobile ? t('closeSidebar') : this.collapsed ? t('expandSidebar') : t('collapseSidebar');
    this.toggleButton.setAttribute('aria-label', label);
    this.toggleButton.setAttribute('aria-expanded', String(this.mobile ? this.open : !this.collapsed));
  }

  private watchBreakpoint(): void {
    if (!isBrowser || typeof matchMedia !== 'function') return;
    this.media?.removeEventListener('change', this.onMediaChange);
    this.media = matchMedia(`(max-width: ${this.breakpoint - 0.02}px)`);
    this.media.addEventListener('change', this.onMediaChange);
    this.onMediaChange();
  }

  private onMediaChange = (): void => {
    const mobile = Boolean(this.media?.matches);
    if (mobile === this.mobile) return;
    this.toggleAttribute('mobile', mobile);
    if (!mobile) {
      this.deactivateDrawer();
      this.removeAttribute('open');
    }
    this.hideFlyout();
    this.syncToggleLabel();
  };

  private activateDrawer(): void {
    this.returnFocus = deepActiveElement();
    lockScroll();
    this.setAttribute('aria-modal', 'true');
    this.releaseTrap?.();
    this.releaseTrap = trapFocus(this, () => this.drawerFocusables());
    requestAnimationFrame(() => (this.drawerFocusables()[0] ?? this.toggleButton).focus());
    this.emit('hn-open');
  }

  private deactivateDrawer(notify = false): void {
    if (!this.releaseTrap) return;
    this.releaseTrap();
    this.releaseTrap = undefined;
    this.removeAttribute('aria-modal');
    unlockScroll();
    const target = this.returnFocus;
    this.returnFocus = null;
    if (target instanceof HTMLElement && target.isConnected) target.focus();
    if (notify) this.emit('hn-close');
  }

  private drawerFocusables(): HTMLElement[] {
    return [...getFocusable(this), this.toggleButton];
  }

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.mobile && this.open) {
      event.stopPropagation();
      this.hide();
    }
  };

  /** Sur mobile, suivre un lien referme le tiroir. */
  private onClick = (event: MouseEvent): void => {
    if (!this.mobile || !this.open) return;
    const link = (event.target as Element).closest?.('a[href]');
    if (link && this.contains(link)) this.hide();
  };

  private itemFrom(event: Event): Element | null {
    if (!this.collapsed || this.mobile) return null;
    return (event.target as Element).closest?.('.hn-sidebar-item') ?? null;
  }

  private onPointerOver = (event: Event): void => {
    const item = this.itemFrom(event);
    if (item && item !== this.flyoutTarget) this.showFlyout(item);
  };

  private onFocusIn = (event: Event): void => {
    const item = this.itemFrom(event);
    if (item && (item as HTMLElement).matches(':focus-visible')) this.showFlyout(item);
  };

  private onPointerOut = (event: Event): void => {
    const next = (event as PointerEvent | FocusEvent).relatedTarget as Element | null;
    if (this.flyoutTarget && next && this.flyoutTarget.contains(next)) return;
    this.hideFlyout();
  };

  private showFlyout(item: Element): void {
    const text = item.getAttribute('aria-label') || item.querySelector('.hn-sidebar-item__label')?.textContent?.trim();
    if (!text) return;
    this.flyoutTarget = item;
    this.flyout.textContent = text;
    showPopover(this.flyout);
    applyPosition(this.flyout, computePosition(item, this.flyout, { placement: this.getAttribute('placement') === 'end' ? 'left' : 'right', offset: 12 }));
  }

  private hideFlyout(): void {
    this.flyoutTarget = undefined;
    if (this.flyout) hidePopover(this.flyout);
  }
}

define(HnSidebar.tagName, HnSidebar);
